import React, {
  useCallback,
  useState
} from 'react'
import {
  Box,
  Divider,
  Typography
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { CartItem } from '@/modules/cart/types/cart'
import { getFeaturedImage } from '@/modules/product/utils'
import ChronoCard from '@/layouts/core/components/ChronoCard'
import AddToCart from '@/modules/cart/components/AddToCart'
import { HasProduct } from '@/modules/product/types/product'
import { ProductImages } from '@/modules/product/components/ProductImages'
import { ProductPrice } from '@/modules/product/components/ProductPrice'
import CartItemOptions from '@/modules/cart/components/CartItemOptions'
import CartItemQuantity from '@/modules/cart/components/CartItemQuantity'
import CartItemShippingType from '@/modules/cart/components/CartItemShippingType'
import { useEvent } from '@/modules/event/eventSlice'

const useStyles = makeStyles((theme: Theme) => ({
  cartOptions: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 20,
      width: '100%',
      justifyContent: 'end'
    },
  },
  sizeChart: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 10,
    },
  },
  addToCart: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 20
    }
  },
}))

const ProductPanelContent = ({ product, option }: CartItem) => {
  const event = useEvent()
  const { internationalShippingAmount } = event
  const { showInventoryRemaining } = product
  const shippingMessage = !internationalShippingAmount ? '* Available for US Shipment Only' : null

  return (
    <Box
      data-cy={`product_panel_${product.sku}`}
      display={'flex'} 
      flexDirection={'column'}>
      
      <Typography
        data-cy={`product_name_${product.sku}`}
        variant={'h4'}>
        {product.name}
      </Typography>

      <Divider
        sx={{
          width: '100%',
          marginTop: 2,
          marginBottom: 2
        }}/>

      {product.description &&
        <Typography
          data-cy={`product_desc_${product.sku}`}>
          Description: {product.description}
        </Typography>
      }

      <Typography
        data-cy={`product_sku_${product.sku}`}
        variant={'subtitle2'}>
        SKU # {product.sku}
      </Typography>

      {product.deliveryType !== 'EVENT_PICKUP' && shippingMessage &&
        <Typography
          data-cy={`product_ship_${product.sku}`}
          variant={'caption'}>
          {shippingMessage}
        </Typography>
      }

      {showInventoryRemaining &&
        <Typography
          data-cy={`product_inv_${product.sku}`}
          variant={'caption'}>
          There are ({option?.remaining}) items available
        </Typography>
      }

    </Box>
  )
}

type ProductPanelControlsProps = {
  setOption: (ProductOption) => any
  setQuantity: (number) => any
  setShippingType: (StoreProductDeliveryType) => any
} & HasProduct & CartItem

const ProductPanelControls = (props: ProductPanelControlsProps) => {
  const classes = useStyles()
  const cartItem: CartItem = props
  const { product, setOption, setQuantity, setShippingType } = props
  const onOptionUpdate = useCallback((optId) => {
    const option = product.options.find(o => o.id === optId)
    setOption(option)
  }, [product, setOption])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'space-between'}
      alignItems={'end'}
      height={'100%'}>

      <Box
        className={classes.cartOptions}
        display={'flex'}
        flexDirection={'row'}
        marginLeft={4}
        marginBottom={1}>

        <CartItemOptions
          {...cartItem}
          onUpdate={onOptionUpdate}/>

        <CartItemQuantity
          {...cartItem}
          onUpdate={setQuantity}/>
      </Box>

      <CartItemShippingType
        {...cartItem}
        onUpdate={setShippingType}/>
      
      <Box className={classes.addToCart}>
        <AddToCart {...cartItem} />
      </Box>

    </Box>
  )
}

const ProductPanel = ({ product }: HasProduct) => {
  const featuredImage = getFeaturedImage(product)
  const [cartItem, setCartItem] = useState<CartItem>({
    product,
    option: product.options[0],
    quantity: 1,
    shippingType: product.deliveryType === 'USER_DECIDES' ? '' : product.deliveryType
  })
  const setShippingType = useCallback((shippingType) => {
    setCartItem({
      ...cartItem,
      shippingType
    })
  }, [cartItem])
  const setOption = useCallback((option) => {
    setCartItem({
      ...cartItem,
      option
    })
  }, [cartItem])
  const setQuantity = useCallback((quantity) => {
    setCartItem({
      ...cartItem,
      quantity
    })
  }, [cartItem])

  return (
    <ChronoCard
      data-cy={'product_' + product.sku}
      className={'productPanel'}
      paper={{
        elevation: 3
      }}
      image={{
        src: (featuredImage || ''),
        title: product.name
      }}
      subImage={
        <Box 
          display={'flex'} 
          flexDirection={'column'} 
          alignItems={'center'}
          marginTop={1}>
          
          <ProductImages product={product} sx={{ marginBottom: 2 }}/>
          <ProductPrice {...cartItem} />
          
        </Box>
      }
      content={
        <ProductPanelContent {...cartItem} />
      }
      controls={
        <ProductPanelControls
          product={product}
          {...cartItem}
          setShippingType={setShippingType}
          setOption={setOption}
          setQuantity={setQuantity}
        />
      }
    />
  )
}

export default ProductPanel